/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { useState, useRef, useEffect, lazy, Suspense } from "react";
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { convertDate } from "@Services/luxonHelper";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axiosWrapper from "@AxiosWrapper";
import Forbidden from "@Pages/Error/Forbidden";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { getTransmittalDataUrl } from "@Services/dataManager";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import CreateIcon from "@Components/StickySidebarViewer/assets/CreateIcon";
import { setShowStickySidebar, setSidebarSelectedCell } from "@Reducers/stickySidebar";
import { renderUserTag, renderUserTagGroup } from "@CubeComponents/html/userTagDom";
const CreateTransmittal = lazy(() => import("../CreateTransmittal/CreateTransmittal"));
import SlickGrid1, {
  withDynamicColumns,
  type SlickGrid1Ref,
  type ValidatedColumnDefinition,
  type ToolbarItem,
  type LayoutData,
  type QueryPredicate,
  type FilterCondition,
} from "@CubeComponents/html/SlickGrid1";
import { setBreadCrumbData } from "@Reducers/breadcrumbsdata";
import {
  getProjectDashboardPageUrl,
  getProjectTransmittalsPageUrl,
  getSingleTransmittalPageUrl,
} from "@Features/projects/utils/projectUrls";
import { getSubColumns } from "@CubeComponents/html/SlickGrid1/customFields";
import ProjectModuleNoAccessIllustration from "@Components/ProjectModulesAccessIllustration";
import useUserModuleAccess from "@Services/userModuleAccessBySheetHook.js";
import GridPageLayout from "@CubeComponents/html/GridPageLayout";
import type { TranslationKey } from "@i18n";
import useTransmittalPermissions from "../../../hooks/useTransmittalPermissions";
import type { UnknownAction } from "@reduxjs/toolkit";
import type { ThunkDispatch } from "redux-thunk";
import type { RootState } from "src/reducers";
import type { GetFormTemplatesForProjectResponse } from "@ServerTypes/transmittals/transmittals.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type SlickGridColumn = {
  id: string;
  field: string;
  name: string;
  filterType?: string;
  minWidth?: number;
  /** SlickGrid formatter args: (rowIndex, cellIndex, cellValue, columnDef, dataContext). */
  formatter?: (
    row: unknown,
    cell: unknown,
    value: unknown,
    columnDef: unknown,
    dataContext: TransmittalListItem,
  ) => unknown;
  /** SlickGrid hands the row itself to the value getters, the same object the formatters get. */
  _valueGetter?: (args: TransmittalListItem) => unknown;
  hidden?: boolean;
  [key: string]: unknown;
};

type TransmittalListItem = {
  id?: string;
  project?: { _id?: string; description?: string };
  toSpaceIds?: Array<{ spaceName?: string }>;
  createdBy?: { _id?: ObjectIdString; fullName?: string };
  createdDate?: number;
  sentToUsers?: ObjectIdString[];
  sentToUserNames?: string[];
};

type GridQuery = {
  portalId?: string;
  projectId?: string;
  where?: unknown[];
  [key: string]: unknown;
};

function TransmittalsList() {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, UnknownAction>>();
  const params = useParams();
  const navigate = useNavigate();
  const moduleAccess = useUserModuleAccess();

  const dataGrid = useRef<SlickGrid1Ref<TransmittalListItem> | null>(null);
  const [searchParams] = useSearchParams();
  const byPortal = searchParams.get("byPortal");

  const projectData = useSelector((state: RootState) => state.projectData);
  const portalData = useSelector((state: RootState) => state.portalData);
  const userDetails = useSelector((state: RootState) => state.userDetails);

  // Use centralized permissions hook
  const { isReady, hasPortalSeat, canViewModule, canCreateTransmittal } = useTransmittalPermissions({
    moduleAccess,
    byPortal,
  });

  const [customFields, setCustomFields] = useState<GetFormTemplatesForProjectResponse>([]);
  const [openCreateTransmittalModal, setOpenCreateTransmittalModal] = useState(false);
  const [gridQuery, setGridQuery] = useState<GridQuery | null>(null);
  const [columnDefinitions, setColumnDefinitions] = useState<ValidatedColumnDefinition<TransmittalListItem>[]>([]);
  const [preAppliedFilters, setPreAppliedFilters] = useState<Record<string, FilterCondition>>({});

  useEffect(() => {
    if (byPortal) {
      dispatch(setShowStickySidebar(false));
      dispatch(setSidebarSelectedCell(""));
    } else {
      dispatch(setShowStickySidebar(true));
      dispatch(setSidebarSelectedCell("Transmittals"));
    }
  }, [byPortal]);

  useEffect(() => {
    if (byPortal) {
      document.title = `Transmittals - ${portalData.name} `;
      setGridQuery({
        portalId: portalData.id,
      });
    } else if (projectData.id) {
      document.title = `${t("Transmittals")} - ${projectData.description} `;
      setGridQuery({
        projectId: projectData.id,
        portalId: portalData.id,
      });
      axiosWrapper
        .post<GetFormTemplatesForProjectResponse>(`${getBackendUrl()}/transmittals/getFormTemplatesForProject`, {
          projectId: projectData.id,
        })
        .then((res: { data?: GetFormTemplatesForProjectResponse }) => {
          const customFieldsData = res.data || [];
          setCustomFields(customFieldsData);
        });
    }
  }, [projectData, portalData, byPortal]);

  useEffect(() => {
    dispatch(
      setBreadCrumbData(
        projectData.id && !byPortal
          ? [
              { text: t("Projects"), link: "/projects" },
              {
                text: projectData.description,
                link: getProjectDashboardPageUrl(projectData.id),
                showTooltip: true,
                tooltipData: projectData.description,
              },
              {
                text: t("Transmittals"),
                link: getProjectTransmittalsPageUrl(projectData.id),
                showTooltip: false,
              },
            ]
          : byPortal
            ? [{ text: t("Portal Dashboard"), link: "/dashboard" }, { text: t("Portal Transmittals") }]
            : [],
      ),
    );
  }, [projectData, byPortal]);

  useEffect(() => {
    const baseColumns: SlickGridColumn[] = [
      {
        id: "indexAtProjectLevel",
        field: "indexAtProjectLevel",
        name: t("ID"),
        filterType: "Number",
        minWidth: 150,
      },
      ...(byPortal
        ? [
            {
              id: "project.description",
              field: "project.description",
              name: t("Project Name"),
              _valueGetter: (args: TransmittalListItem) => args.project?.description,
              formatter: (_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: TransmittalListItem) =>
                params.project?.description ?? "",
              filterType: "Text",
              minWidth: 150,
            },
          ]
        : []),
      {
        id: "subject",
        field: "subject",
        name: t("Subject"),
        filterType: "Text",
        minWidth: 150,
      },
      {
        id: "from",
        field: "from",
        name: t("From Space"),
        filterType: "Text",
        minWidth: 150,
      },
      {
        id: "toSpaceIds",
        field: "toSpaceIds.spaceName",
        name: t("To Space"),
        formatter: formatToSpaceIds,
        minWidth: 150,
        filterType: "MultiSelect",
        _valueGetter: (args: TransmittalListItem) => (args.toSpaceIds ?? []).map((space) => space.spaceName),
        _excelValueGetter: (args: TransmittalListItem) =>
          (args.toSpaceIds ?? []).map((space) => space.spaceName).join(", "),
      },
      {
        id: "createdBy",
        field: "createdBy.fullName",
        name: t("Sent By"),
        minWidth: 150,
        _valueGetter: (args: TransmittalListItem) => args.createdBy?.fullName,
        formatter: formatCreatedBy,
        filterType: "Text",
      },
      {
        id: "sentToUsers",
        field: "sentToUsers",
        name: t("Sent To Users"),
        minWidth: 150,
        disableSorting: true,
        disableGrouping: true,
        formatter: formatSentToUsers,
        filterType: "MultiSelect",
        _valueGetter: (args: TransmittalListItem) => args.sentToUserNames ?? [],
        _excelValueGetter: (args: TransmittalListItem) => (args.sentToUserNames ?? []).join(", ") || "-",
      },
      {
        id: "createdDate",
        field: "createdDate",
        name: t("Sent On"),
        formatter: formatCreatedDate,
        filterType: "Date",
        minWidth: 150,
        _valueGetter: (args: TransmittalListItem) => {
          const date = args.createdDate;
          return date ? convertDate(Number(date), "dateForTooltip") : "";
        },
      },
    ];

    // `getSubColumns` builds these from a form template at runtime, so their `field` keys
    // (`customFields.<questionId>`) can never be `keyof TransmittalListItem` — they stay at the
    // record shape it returns and are branded by `withDynamicColumns`, the documented escape hatch
    // for exactly this mix.
    let allCustomColumns: Record<string, unknown>[] = [];

    if (customFields.length > 0) {
      customFields.forEach((section: GetFormTemplatesForProjectResponse[number]) => {
        // `getSubColumns` declares its section as the FULL `FormTemplateField`, while this endpoint
        // returns a projection of it (`_id`, `sectionHeading?`, and three keys per question field).
        // The intersection bridges the two without reshaping the data; only the keys the projection
        // carries are ever read. Fix: declare `getSubColumns`'s parameter as the subset it reads.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- getFormTemplatesForProjectResponse (packages/server/transmittals/transmittals.schema.ts) is a strict subset of FormTemplateField, and getSubColumns reads only keys that subset carries
        const sectionColumns = getSubColumns(section as typeof section & Parameters<typeof getSubColumns>[0]);
        allCustomColumns = [...allCustomColumns, ...sectionColumns];
      });
    }
    setColumnDefinitions(withDynamicColumns<TransmittalListItem>([...baseColumns, ...allCustomColumns]));
  }, [customFields]);

  const onClickLayout = (layoutParams: LayoutData) => {
    if (layoutParams.queryConditions?.where) {
      const processedFilters = layoutParams.queryConditions.where.map(
        (filter: QueryPredicate | Record<string, unknown>) => {
          if (filter.value === "CURRENT_USER" && userDetails._id) {
            return { ...filter, value: userDetails._id };
          }
          return filter;
        },
      );
      setGridQuery((prevQuery) => ({
        ...prevQuery,
        where: processedFilters,
      }));
    } else {
      setGridQuery((prevQuery) => {
        const newQuery = { ...prevQuery };
        delete newQuery.where;
        return newQuery;
      });
    }

    if (layoutParams.filterConditions && Object.keys(layoutParams.filterConditions).length) {
      setPreAppliedFilters(layoutParams.filterConditions);
    } else {
      setPreAppliedFilters({});
    }
  };

  const createTransmittalsButton = () => (
    <div className="ms-4" data-testid="createBtn">
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={() => setOpenCreateTransmittalModal(true)}>
        <span className="flex items-center justify-start">
          <CreateIcon size="15" className="me-1" />
          {t("Create")}
        </span>
      </ButtonComponent>
    </div>
  );

  const toolBar: ToolbarItem[] = [
    {
      id: "create_transmittal",
      template: createTransmittalsButton,
      // `byPortal` is the raw `?byPortal` query-string value (`string | null`); the toolbar reads
      // `disabled` for truthiness, so it is narrowed to that here instead of at the JSX boundary.
      disabled: !canCreateTransmittal || Boolean(byPortal),
    },
  ];

  const formatToSpaceIds = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: TransmittalListItem) => {
    const toSpaceNames = (params.toSpaceIds ?? []).map((space) => space.spaceName).join(", ");
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(<div>{toSpaceNames}</div>);
    return container;
  };

  const formatCreatedDate = (
    _data: unknown,
    _value: unknown,
    _a: unknown,
    _b: unknown,
    params: TransmittalListItem,
  ) => {
    const dateFormat = convertDate(Number(params.createdDate), "dateForTooltip");
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <ToolTipComponent position="bottom" content={dateFormat}>
        {convertDate(Number(params.createdDate), "dateForText")}
      </ToolTipComponent>,
    );
    return container;
  };

  const formatCreatedBy = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: TransmittalListItem) => {
    const container = document.createElement("div");
    renderUserTag(container, { id: params.createdBy?._id });
    return container;
  };

  const formatSentToUsers = (
    _data: unknown,
    _value: unknown,
    _a: unknown,
    _b: unknown,
    params: TransmittalListItem,
  ) => {
    const container = document.createElement("div");
    const userIds = params.sentToUsers;
    if (!userIds || !userIds.length) {
      container.textContent = "-";
      return container;
    }
    if (userIds.length === 1) {
      renderUserTag(container, { id: userIds[0] });
    } else {
      renderUserTagGroup(container, {
        ids: userIds,
        maxAvatars: 3,
        size: "XXS",
        enableDetailsModal: true,
        detailsModalHeader: t("Sent To Users"),
      });
    }
    return container;
  };

  const recordClick = (args: { detail: { args: { row: number; cell: number } } }) => {
    const rowIndex = args.detail.args.row;
    const clickedRow = dataGrid.current?.slickGrid.getDataItem(rowIndex);
    if (args.detail.args.cell != 0 && clickedRow?.project?._id && clickedRow?.id) {
      navigate(getSingleTransmittalPageUrl(clickedRow?.project?._id, clickedRow?.id));
    }
  };

  const getTransmittalsGrid = () => (
    <SlickGrid1
      ref={dataGrid}
      columnDefinitions={columnDefinitions}
      remoteDataSource={getTransmittalDataUrl}
      // KNOWN GAP, preserved verbatim: `gridQuery` is `null` until the project/portal ids arrive,
      // and the grid is rendered before then. Erased assertion.
      query={gridQuery!}
      onClick={recordClick}
      infiniteScroll
      toolbar={toolBar}
      showToolbarBorder
      gridOptions={{ forceFitColumns: true }}
      enableSortingOption
      enableSearchBar
      enableFilteringOption
      enableColumnChooser
      enableGroupingOption
      excelExport
      enableViews={!byPortal && Boolean(params.projectId)}
      {...(!byPortal &&
        Boolean(params.projectId) && {
          viewsOptions: {
            headerName: "Views",
            name: "View",
            resourceType: "TRANSMITTALS",
            selectedLayoutKey: "TRANSMITTALS_SELECTED_LAYOUT",
            fetchParams: {
              projectId: params.projectId,
            },
          },
        })}
      onClickLayout={onClickLayout}
      preAppliedFilters={preAppliedFilters}
      disableGridMargin
    />
  );

  const refreshGrid = () => {
    if (dataGrid.current) {
      dataGrid.current.refresh?.();
    }
  };

  return (
    <div className="w-full overflow-auto h-full flex">
      {isReady &&
        (!hasPortalSeat ? (
          <ProjectModuleNoAccessIllustration />
        ) : (
          <div className={"overflow-auto w-full h-full"}>
            {!canViewModule || !columnDefinitions.length ? (
              <Forbidden />
            ) : (
              <GridPageLayout
                title={"Transmittals" as TranslationKey}
                description={"List of all transmittals" as TranslationKey}
              >
                {getTransmittalsGrid()}
              </GridPageLayout>
            )}
          </div>
        ))}
      {openCreateTransmittalModal && (
        <Suspense fallback={null}>
          <CreateTransmittal
            isOpen={openCreateTransmittalModal}
            onClose={() => {
              setOpenCreateTransmittalModal(false);
              refreshGrid();
            }}
            projectId={params.projectId!}
          />
        </Suspense>
      )}
    </div>
  );
}

export default TransmittalsList;
